/**
 * extractSetOperatorBranches — token-aware splitter for top-level
 * UNION / INTERSECT / EXCEPT chains (Oracle also MINUS, gated to
 * `engine === 'oracle'`). Pure function; uses `tokenSkip` so
 * dialect-specific strings / comments / quoted identifiers / dollar-tags
 * are skipped. Paren-depth tracking ensures set-operator keywords inside
 * subqueries / CTE bodies / derived tables stay invisible (those are
 * handled either by recursive `<SelectRenderer>` mounts via subqueries
 * or by the precedence detector — see `detectSetOperatorPrecedence.ts`).
 *
 * For each separator we record the kind (`UNION` / `INTERSECT` / `EXCEPT`)
 * plus an `isAll` flag (true when the optional `ALL` modifier follows).
 * `DISTINCT` is the implicit default and is consumed without surfacing
 * (so `INTERSECT DISTINCT` renders the same as `INTERSECT`). Branches are
 * the (possibly trimmed) source spans between separators — left-to-right
 * in declaration order, including any leading `WITH` clause that belongs
 * to the first branch (the parser owns the semantic question of which
 * branch the WITH attaches to; v1 keeps it inside the first branch's
 * range, where the recursive `<SelectRenderer>` will render it via
 * `extractCtes`).
 */
import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';

export type SetOpKind = 'UNION' | 'INTERSECT' | 'EXCEPT' | 'MINUS';

export interface SetOperatorSeparator {
  readonly kind: SetOpKind;
  readonly isAll: boolean;
  readonly keywordStart: number;
  readonly keywordEnd: number;
}

export interface SetOperatorBranchSpan {
  readonly start: number;
  readonly end: number;
}

export interface SetOperatorOutline {
  readonly hasSetOperators: boolean;
  readonly branches: readonly SetOperatorBranchSpan[];
  readonly separators: readonly SetOperatorSeparator[];
}

const SET_OP_KINDS: readonly SetOpKind[] = ['UNION', 'INTERSECT', 'EXCEPT'];
// `MINUS` is an Oracle-only set operator; on every other engine `minus` is a
// legal column alias, so it is recognized only when `engine === 'oracle'`. Keep
// this gate in lockstep with the resolver scanner (selectShape.ts) and the
// precedence detector so resolver/UI separator counts stay 1:1.
const ORACLE_SET_OP_KINDS: readonly SetOpKind[] = ['UNION', 'INTERSECT', 'EXCEPT', 'MINUS'];
const IDENT_CONT = /[A-Za-z0-9_$]/;

export function isWordBoundary(source: string, idx: number): boolean {
  if (idx < 0 || idx >= source.length) return true;
  return !IDENT_CONT.test(source[idx]);
}

function matchKeywordIgnoreCase(
  source: string,
  i: number,
  end: number,
  kw: string,
): boolean {
  if (i + kw.length > end) return false;
  for (let k = 0; k < kw.length; k++) {
    const ch = source[i + k];
    if (ch !== kw[k] && (!ch || ch.toUpperCase() !== kw[k])) return false;
  }
  return isWordBoundary(source, i - 1) && isWordBoundary(source, i + kw.length);
}

function tryMatchSetOpAt(
  source: string,
  i: number,
  end: number,
  kinds: readonly SetOpKind[],
): { kind: SetOpKind; keywordEnd: number } | null {
  for (const kw of kinds) {
    if (matchKeywordIgnoreCase(source, i, end, kw)) {
      return { kind: kw, keywordEnd: i + kw.length };
    }
  }
  return null;
}

function tryConsumeAllOrDistinct(
  source: string,
  from: number,
  end: number,
): { keywordEnd: number; isAll: boolean } | null {
  let j = from;
  while (j < end && /\s/.test(source[j])) j++;
  if (matchKeywordIgnoreCase(source, j, end, 'ALL')) {
    return { keywordEnd: j + 3, isAll: true };
  }
  if (matchKeywordIgnoreCase(source, j, end, 'DISTINCT')) {
    return { keywordEnd: j + 8, isAll: false };
  }
  return null;
}

export function extractSetOperatorBranches(
  source: string,
  range: SqlRange,
  options: ParseOptions,
): SetOperatorOutline {
  const start = Math.max(0, range.startOffset);
  const end = Math.max(start, Math.min(source.length, range.endOffset));
  const ctx = skipContextFor(options);
  const kinds = options.engine === 'oracle' ? ORACLE_SET_OP_KINDS : SET_OP_KINDS;

  const separators: SetOperatorSeparator[] = [];
  let i = start;
  let depth = 0;

  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      i = skipped > i ? skipped : i + 1;
      continue;
    }
    const ch = source[i];
    if (ch === '(') {
      depth++;
      i++;
      continue;
    }
    if (ch === ')') {
      if (depth > 0) depth--;
      i++;
      continue;
    }
    if (depth === 0 && /[A-Za-z]/.test(ch)) {
      const m = tryMatchSetOpAt(source, i, end, kinds);
      if (m) {
        const modifier = tryConsumeAllOrDistinct(source, m.keywordEnd, end);
        const keywordEnd = modifier ? modifier.keywordEnd : m.keywordEnd;
        const isAll = modifier ? modifier.isAll : false;
        separators.push({
          kind: m.kind,
          isAll,
          keywordStart: i,
          keywordEnd,
        });
        i = keywordEnd;
        continue;
      }
    }
    i++;
  }

  if (separators.length === 0) {
    return { hasSetOperators: false, branches: [], separators: [] };
  }

  const branches: SetOperatorBranchSpan[] = [];
  let cursor = start;
  for (const sep of separators) {
    branches.push({ start: cursor, end: sep.keywordStart });
    cursor = sep.keywordEnd;
  }
  branches.push({ start: cursor, end });

  return { hasSetOperators: true, branches, separators };
}
